import React from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useReactors } from "@/hooks/useReactors";
import { ReactorRow } from "@/components/social/reactions/ReactorRow";
import { REACTION_MAP } from "@/lib/reactions";

const PREVIEW = 5;

/**
 * Lightweight "who gave this reaction" preview on an existing reaction chip.
 * Hover only — the chip's click keeps giving/removing/changing the reaction, so every
 * interaction inside the card stops propagation. Fetches at most 5 people.
 */
export function ReactionHoverPreview({ children, target, emojiKey, count, onSeeAll }) {
  const [open, setOpen] = React.useState(false);
  const { items, loading } = useReactors(target, emojiKey, open, PREVIEW);
  const meta = REACTION_MAP[emojiKey];
  const more = Math.max(0, (count || 0) - items.length);

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={350} closeDelay={120}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        align="start" className="w-72 p-2"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        data-testid={`reaction-hover-preview-${emojiKey}`}
      >
        <p className="px-2 pb-1 text-xs font-[600] uppercase tracking-wide text-muted-foreground">
          {meta?.label || "Reactions"}
        </p>
        {loading && items.length === 0 && (
          <p className="px-2 py-2 text-sm text-muted-foreground">Loading…</p>
        )}
        {items.map((p) => (
          <ReactorRow key={p.user_id} person={{ ...p, emoji: undefined }} />
        ))}
        {more > 0 && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onSeeAll?.(emojiKey); }}
            className="mt-1 w-full rounded-md px-2 py-2 text-left text-sm font-[560] text-primary transition-colors hover:bg-secondary/60"
            data-testid={`reaction-hover-more-${emojiKey}`}
          >
            +{more} other{more === 1 ? "" : "s"}
          </button>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

export default ReactionHoverPreview;
